import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useMemo, useState } from "react";
import { Pressable, ScrollView, StyleSheet, Text, TextInput, View } from "react-native";

import {
  type Au13Input,
  Au13InputSchema,
  CCS_CARE_TYPES,
  type CcsCareType,
  careTypeLabel,
} from "@aucash/rules/au13";

import { useAuth } from "../../src/auth";
import { AppBar, Button, Coin, Eyebrow } from "../../src/components/brand";
import { Field, MoneyInput, Segmented, Stepper, YesNo } from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space } from "../../src/theme";

// AU-13 input screen — Child Care Subsidy estimate + backdated reclaim
// (spec §7.3). Two modes: a current-year estimate, or a reclaim for a past
// care date (which resolves against the rules in force then — FY2023-24 splits
// at the 10 Jul 2023 Cheaper Child Care reform). The reclaim mode reveals a
// care-date field; everything else is shared. hourlyFee/hoursUsed are per
// fortnight for one child.

type Mode = "current" | "reclaim";

const CARE_TYPE_OPTIONS = CCS_CARE_TYPES.map((value) => ({
  value,
  label: careTypeLabel(value),
}));

export default function Au13Index(): JSX.Element {
  const router = useRouter();
  const { status } = useAuth();

  const [mode, setMode] = useState<Mode>("current");
  const [familyIncome, setFamilyIncome] = useState("");
  const [careType, setCareType] = useState<CcsCareType>("centre_based_day_care");
  const [hourlyFee, setHourlyFee] = useState("");
  const [hoursUsed, setHoursUsed] = useState(72);
  const [activityHours, setActivityHours] = useState(48);
  const [schoolAged, setSchoolAged] = useState(false);
  const [indigenousChild, setIndigenousChild] = useState(false);
  const [secondChild, setSecondChild] = useState(false);
  const [careDate, setCareDate] = useState("");

  const [errors, setErrors] = useState<{ income?: string; date?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const input = useMemo<Au13Input | null>(() => {
    const raw = {
      familyIncome: Number(familyIncome),
      hoursUsed,
      careType,
      hourlyFee: Number(hourlyFee),
      activityHours,
      schoolAged,
      indigenousChild,
      childRank: secondChild ? 2 : 1,
    };
    const parsed = Au13InputSchema.safeParse(raw);
    return parsed.success ? parsed.data : null;
  }, [
    familyIncome,
    hoursUsed,
    careType,
    hourlyFee,
    activityHours,
    schoolAged,
    indigenousChild,
    secondChild,
  ]);

  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(careDate);

  const onSubmit = (): void => {
    const next: { income?: string; date?: string } = {};
    if (familyIncome.trim() === "") {
      next.income = "Enter your family income for this year.";
    }
    if (mode === "reclaim" && !validDate) {
      next.date = "Enter the care date as YYYY-MM-DD (e.g. 2024-03-15).";
    }
    setErrors(next);
    if (Object.keys(next).length > 0 || input === null || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    trpc.calc.au13
      .mutate({
        input,
        ...(mode === "reclaim" ? { careDate } : {}),
      })
      .then(({ id }) => {
        router.push({ pathname: "/au13/result", params: { id } });
      })
      .catch((err: unknown) => {
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't save your result.",
        );
      })
      .finally(() => setSubmitting(false));
  };

  // OSHC / centre-based switch on school age; FDC and IHC ignore it.
  const showSchoolAged =
    careType === "centre_based_day_care" ||
    careType === "outside_school_hours_care";

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Childcare" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
            {status === "authed" ? (
              <Pressable onPress={() => router.push("/my-results")} accessibilityRole="button">
                <Text style={styles.navLink}>My results</Text>
              </Pressable>
            ) : status === "anon" ? (
              <Pressable onPress={() => router.push("/auth")} accessibilityRole="button">
                <Text style={styles.navLink}>Sign in</Text>
              </Pressable>
            ) : null}
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>Subsidy &amp; past-year reclaim</Eyebrow>
            <Text style={styles.title}>Estimate your Child Care Subsidy</Text>
            <Text style={styles.subtitle}>
              Estimate your Child Care Subsidy per fortnight, or check a past care
              date against the rules that applied then, from current Services
              Australia and Department of Education figures.
            </Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field label="What do you want to work out?">
            <Segmented<Mode>
              options={[
                { value: "current", label: "This year" },
                { value: "reclaim", label: "A past year" },
              ]}
              value={mode}
              onChange={setMode}
            />
          </Field>

          {mode === "reclaim" ? (
            <Field
              label="Date of care"
              help="We resolve your subsidy against the rules in force on this date. FY2023-24 changed on 10 July 2023."
              error={errors.date}
            >
              <TextInput
                value={careDate}
                onChangeText={(t) => setCareDate(t.replace(/[^0-9-]/g, ""))}
                keyboardType="numbers-and-punctuation"
                maxLength={10}
                placeholder="YYYY-MM-DD"
                placeholderTextColor={colors.muted}
                style={styles.dateInput}
                testID="input-care-date"
                accessibilityLabel="Date of care, year month day"
              />
            </Field>
          ) : null}

          <Field
            label="Combined family income"
            help="Your adjusted taxable income (plus your partner's, if you have one). Sets your subsidy percentage."
            error={errors.income}
          >
            <MoneyInput
              value={familyIncome}
              onChange={setFamilyIncome}
              testID="input-family-income"
              accessibilityLabel="Combined family income"
            />
          </Field>

          <Field label="Care type">
            <Segmented<CcsCareType>
              options={CARE_TYPE_OPTIONS}
              value={careType}
              onChange={setCareType}
            />
          </Field>

          <Field
            label="Hourly fee the service charges"
            help="CCS pays your percentage of the lower of this fee and the hourly rate cap for your care type."
          >
            <MoneyInput
              value={hourlyFee}
              onChange={setHourlyFee}
              testID="input-hourly-fee"
              accessibilityLabel="Hourly fee the service charges"
            />
          </Field>

          <Field
            label="Hours of care per fortnight"
            help="For this child. We subsidise the lesser of this and your entitlement."
          >
            <Stepper value={hoursUsed} onChange={setHoursUsed} min={0} max={200} suffix="hrs" accessibilityLabel="Hours of care per fortnight" />
          </Field>

          <Field
            label="Recognised activity hours per fortnight"
            help="Work, study or volunteering — the lower of you and your partner. Sets how many hours are subsidised."
          >
            <Stepper value={activityHours} onChange={setActivityHours} min={0} max={200} suffix="hrs" accessibilityLabel="Recognised activity hours per fortnight" />
          </Field>

          {showSchoolAged ? (
            <Field label="Is this child school-aged?">
              <YesNo value={schoolAged} onChange={setSchoolAged} />
            </Field>
          ) : null}

          <Field
            label="Second or younger child aged 5 or under?"
            help="Families with more than one young child can get a higher rate for the second and younger children."
          >
            <YesNo value={secondChild} onChange={setSecondChild} />
          </Field>

          <Field label="Aboriginal or Torres Strait Islander child?">
            <YesNo value={indigenousChild} onChange={setIndigenousChild} />
          </Field>

          <Button
            label={
              submitting
                ? "Saving…"
                : mode === "reclaim"
                  ? "Check this care date →"
                  : "See your subsidy →"
            }
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="au13-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  dateInput: {
    borderWidth: 1.5,
    borderColor: colors.lineStrong,
    borderRadius: radius.md,
    paddingVertical: 14,
    paddingHorizontal: space.lg,
    fontFamily: fonts.bodyMedium,
    fontSize: 16,
    color: colors.ink,
    backgroundColor: colors.card,
  },
  submitError: { fontFamily: fonts.bodyMedium, fontSize: 14, color: colors.danger, textAlign: "center" },
});
